import { BooleanInput } from "@angular/cdk/coercion";
import { NgClass } from "@angular/common";
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnDestroy,
  OnInit,
  ViewEncapsulation,
} from "@angular/core";
import { MatButtonModule } from "@angular/material/button";
import { MatDividerModule } from "@angular/material/divider";
import { MatIconModule } from "@angular/material/icon";
import { MatMenuModule } from "@angular/material/menu";
import { Router, RouterLink } from "@angular/router";
import { TranslocoModule } from "@jsverse/transloco";
import { AuthService } from "app/core/auth/auth.service";
import { CookieService } from "app/core/services/cookie.service";
import { UserService } from "app/core/user/user.service";
import { User } from "app/core/user/user.types";
import { SystemManagementStateService } from "app/modules/system-management/system-management-state.service";
import { finalize, Subject, takeUntil, takeWhile, tap, timer } from "rxjs";

import { environment } from "app/environments/environment";
@Component({
  selector: "user",
  templateUrl: "./user.component.html",
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  exportAs: "user",
  imports: [
    MatButtonModule,
    MatMenuModule,
    MatIconModule,
    NgClass,
    MatDividerModule,
    RouterLink,
    TranslocoModule,
  ],
})
export class UserComponent implements OnInit, OnDestroy {
  readonly partnerPortalUrl = environment.partnerPortalUrl;
  private readonly userLanguageCookieKey = "user_selected_language_code";
  /* eslint-disable @typescript-eslint/naming-convention */
  static ngAcceptInputType_showAvatar: BooleanInput;
  /* eslint-enable @typescript-eslint/naming-convention */

  @Input() showAvatar: boolean = true;
  @Input() anonymousMode = false;
  user: User | null = null;
  userMenuOpened = false;

  private _unsubscribeAll: Subject<any> = new Subject<any>();

  countdown: number = 10;
  countdownMapping: any = {
    "=1": "# second",
    other: "# seconds",
  };
  redirected = false;
  /**
   * Constructor
   */
  constructor(
    private _changeDetectorRef: ChangeDetectorRef,
    private _router: Router,
    private _userService: UserService,
    private _authService: AuthService,
    private _systemManagementStateService: SystemManagementStateService,
    private _cookieService: CookieService,
  ) {}

  // -----------------------------------------------------------------------------------------------------
  // @ Lifecycle hooks
  // -----------------------------------------------------------------------------------------------------

  /**
   * On init
   */
  ngOnInit(): void {
    if (this.anonymousMode || !this._authService.getCurrentUser()) {
      this.user = null;
      this._changeDetectorRef.markForCheck();
      return;
    }

    // timer(5000).subscribe(() => {
    this._userService.user$
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe((user: User) => {
        this.user = user;
        this._systemManagementStateService.setUserInfo(user);
        const cookieLangCode = this._cookieService.getCookie(
          this.userLanguageCookieKey,
        );

        if (cookieLangCode === "de" || cookieLangCode === "en-GB") {
          const cookieLanguageLabel =
            cookieLangCode === "de" ? "Deutsch" : "Englisch";
          this._systemManagementStateService.setLanguage(cookieLanguageLabel);
        } else if (user?.languageName) {
          this._systemManagementStateService.setLanguage(user.languageName);
        } else {
          const keepValue = localStorage.getItem("userLang");
          if (keepValue !== null) {
            this._systemManagementStateService.setLanguage(keepValue);
          }
        }
        this._changeDetectorRef.markForCheck();
      });

    // Reuse the user loaded by the login initializer or route guard.
    if (!this._userService.loggedInUser) {
      this._userService.get().subscribe();
    }
    // });
  }

  /**
   * On destroy
   */
  ngOnDestroy(): void {
    // Unsubscribe from all subscriptions
    this._unsubscribeAll.next(null);
    this._unsubscribeAll.complete();
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Public methods
  // -----------------------------------------------------------------------------------------------------

  login(): void {
    void this._authService.login();
  }

  /**
   * Update the user status
   *
   * @param status
   */
  // updateUserStatus(status: string): void {
  //     // Return if user is not available
  //     if (!this.user) {
  //         return;
  //     }

  //     // Update the user
  //     this._userService
  //         .update({
  //             ...this.user,
  //             status,
  //         })
  //         .subscribe();
  // }

  /**
   * Sign out
   */
  signOut(): void {
    //this._router.navigate(["/sign-out"]);

    this._authService.logout();

    // Redirect after the countdown
    timer(1000, 1000)
      .pipe(
        finalize(() => {
          if (!this.redirected) {
            window.open("https://www.tesvolt.com/de/", "");
          }
        }),
        takeWhile(() => this.countdown > 0),
        takeUntil(this._unsubscribeAll),
        tap(() => this.countdown--),
      )
      .subscribe();
  }
}
